import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { 
  GraduationCap, 
  Award, 
  CheckCircle,
  XCircle,
  Search,
  ArrowLeft,
  Calendar,
  User,
  BookOpen
} from "lucide-react";

const VerifyCertificate = () => {
  const { certificateNumber } = useParams();
  const [searchNumber, setSearchNumber] = useState(certificateNumber || "");
  const [certificate, setCertificate] = useState(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (certificateNumber) {
      verifyCertificate(certificateNumber);
    }
  }, [certificateNumber]);

  const verifyCertificate = async (certNumber) => {
    if (!certNumber.trim()) {
      toast.error("Please enter a certificate number");
      return;
    }

    setLoading(true);
    setSearched(true);
    
    try {
      const response = await axios.get(`${API}/certificates/verify/${certNumber.trim()}`);
      setIsValid(response.data.valid);
      setCertificate(response.data.certificate || null);
    } catch (error) {
      setIsValid(false);
      setCertificate(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    verifyCertificate(searchNumber);
  };

  return (
    <div className="min-h-screen bg-stone-50 py-12 px-4">
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-violet-100 rounded-full mb-4">
            <GraduationCap className="h-8 w-8 text-violet-700" />
          </div>
          <h1 
            className="text-3xl font-bold text-stone-900 mb-2"
            style={{ fontFamily: 'Outfit' }}
          >
            Verify Certificate
          </h1>
          <p className="text-stone-500">
            Enter a certificate number to verify its authenticity
          </p>
        </div>

        {/* Search Form */}
        <Card className="bg-white rounded-xl border border-stone-100">
          <CardContent className="p-6">
            <form onSubmit={handleSearch} className="flex gap-3">
              <Input
                value={searchNumber}
                onChange={(e) => setSearchNumber(e.target.value)}
                placeholder="Enter certificate number (e.g., CERT-A1B2C3D4)"
                className="flex-1 h-12"
                data-testid="verify-input"
              />
              <Button 
                type="submit" 
                className="bg-violet-700 hover:bg-violet-800 h-12 px-6"
                disabled={loading}
                data-testid="verify-btn"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Verify
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {searched && !loading && (
          <Card className={`rounded-xl border-2 ${
            isValid ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
          }`} data-testid="verification-result">
            <CardContent className="p-8 text-center">
              {isValid ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-emerald-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-emerald-800 mb-2" style={{ fontFamily: 'Outfit' }}>
                    Valid Certificate
                  </h2>
                  <p className="text-emerald-600 mb-6">
                    This certificate is authentic and was issued by LearnHub.
                  </p>

                  {/* Certificate Details */}
                  <div className="bg-white rounded-xl p-6 text-left space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                        <User className="h-5 w-5 text-violet-700" />
                      </div>
                      <div>
                        <p className="text-sm text-stone-500">Recipient</p>
                        <p className="font-semibold text-stone-900">{certificate.user_name}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                        <BookOpen className="h-5 w-5 text-violet-700" />
                      </div>
                      <div>
                        <p className="text-sm text-stone-500">Course</p>
                        <p className="font-semibold text-stone-900">{certificate.course_title}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-violet-700" />
                      </div>
                      <div>
                        <p className="text-sm text-stone-500">Completion Date</p>
                        <p className="font-semibold text-stone-900">{formatDate(certificate.completed_at)}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                        <Award className="h-5 w-5 text-violet-700" />
                      </div>
                      <div>
                        <p className="text-sm text-stone-500">Certificate Number</p>
                        <p className="font-semibold text-stone-900 font-mono">{certificate.certificate_number}</p>
                      </div>
                    </div>
                  </div>

                  <Link to={`/certificate/${certificate.certificate_id}`}>
                    <Button className="mt-6 bg-violet-700 hover:bg-violet-800 rounded-full">
                      View Full Certificate
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                    <XCircle className="h-8 w-8 text-red-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-red-800 mb-2" style={{ fontFamily: 'Outfit' }}>
                    Certificate Not Found
                  </h2>
                  <p className="text-red-600">
                    We couldn't find a certificate with this number. Please check the number and try again.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Back Link */}
        <div className="text-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-stone-600 hover:text-violet-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyCertificate;
